import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColorHelper, ScaleType } from '@swimlane/ngx-charts';
import { Subject, takeUntil } from 'rxjs';
import { ProjectStatsDataService } from '../../data-access/widgets/project-stats-data.service';

type LegacySeriesItem = { x: string; y: number };
type LegacySeries = { key: string; values: LegacySeriesItem[] };
type NgxStackedDatum = { name: string; series: { name: string; value: number }[] };

@Component({
  selector: 'app-number-of-projects-chart-widget',
  template: `
    <app-dashboard-card title="Projects In EICDataStream">
      <div class="chart-wrap">
        <div class="title h3">{{ chartTitle }}</div>
        <div class="subtitle h4">Analysed by Industry Sector and Region</div>

        <div class="legend-wrap" *ngIf="legendDomain.length > 0">
          <ngx-charts-legend
            class="chart-legend"
            [title]="'Industry Sector'"
            [data]="legendDomain"
            [colors]="legendColors"
            [horizontal]="true">
          </ngx-charts-legend>
        </div>

        <div #chartHost class="chart-host" [class.chart-host--occupied]="loading || chartData.length > 0">
          <ngx-charts-bar-vertical-stacked
            *ngIf="chartData.length > 0"
            [view]="chartView"
            [scheme]="colorScheme"
            [results]="chartData"
            [xAxis]="true"
            [yAxis]="true"
            [legend]="false"
            [yAxisTickFormatting]="yTickFormatting"
            [showXAxisLabel]="true"
            [showYAxisLabel]="true"
            [xAxisLabel]="'Region'"
            [yAxisLabel]="yAxisLabel">
          </ngx-charts-bar-vertical-stacked>

          <div class="chart-loading-overlay" *ngIf="loading">Loading chart data...</div>

          <p class="error" *ngIf="!loading && error">{{ error }}</p>
          <p *ngIf="!loading && !error && chartData.length === 0">No chart data available</p>
        </div>
      </div>

      <mat-button-toggle-group class="project-type-toggle" [value]="type" (change)="setType($event.value)">
        <mat-button-toggle value="Number">Number</mat-button-toggle>
        <mat-button-toggle value="CAPEX">CAPEX</mat-button-toggle>
      </mat-button-toggle-group>
    </app-dashboard-card>
  `,
  styles: [
    `.project-type-toggle { margin-top: 4px; display: inline-flex; width: auto; max-width: 100%; align-self: flex-start; }`,
    `.project-type-toggle .mat-button-toggle { flex: 0 0 auto; min-width: 0; }`,
    `.project-type-toggle .mat-button-toggle-button { width: auto; min-width: 72px; padding: 0 8px; }`,
    `:host ::ng-deep .project-type-toggle .mat-button-toggle-label-content { display: inline-flex; align-items: center; justify-content: center; line-height: 28px; padding: 0 10px; }`,
    `:host ::ng-deep .project-type-toggle.mat-button-toggle-group { border-color: var(--mat-sys-outline-variant, var(--sys-outline-variant)); background: var(--mat-sys-surface-container, var(--sys-surface-container)); }`,
    `:host ::ng-deep .project-type-toggle .mat-button-toggle { color: var(--mat-sys-on-surface-variant, var(--sys-on-surface-variant)); background: transparent; }`,
    `:host ::ng-deep .project-type-toggle .mat-button-toggle.mat-button-toggle-checked { background: var(--mat-sys-secondary, var(--sys-secondary)); color: var(--mat-sys-on-secondary, var(--sys-on-secondary)); }`,
    `.title, .subtitle { display: block; width: 100%; text-align: center; }`,
    `.title { font-size: 20px; line-height: 28px; font-weight: 500; margin: 0; }`,
    `.subtitle { font-size: 14px; line-height: 20px; margin: 0 0 10px; color: var(--mat-sys-on-surface-variant, var(--sys-on-surface-variant)); }`,
    `.chart-wrap {
      position: relative;
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      border-radius: 8px;
      padding: 8px;
    }`,
    `.chart-host { width: 100%; max-width: 100%; overflow: hidden; position: relative; box-sizing: border-box; }`,
    `.chart-host--occupied { min-height: 408px; }`,
    `.legend-wrap {
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      border-radius: 6px;
      padding: 4px 6px;
      margin-bottom: 8px;
    }`,
    `.chart-loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      color: var(--mat-sys-on-surface, var(--sys-on-surface));
      font-size: 12px;
      z-index: 2;
      opacity: 0.92;
    }`,
    `.error { margin: 0; }`,
    `:host ::ng-deep ngx-charts-bar-vertical-stacked,
     :host ::ng-deep .ngx-charts-outer,
     :host ::ng-deep .ngx-charts,
     :host ::ng-deep .ngx-charts .chart,
     :host ::ng-deep .chart-legend {
       background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
     }`,
    `:host ::ng-deep ngx-charts-bar-vertical-stacked .ngx-charts-outer { display: flex; flex-direction: column; }`,
    `:host ::ng-deep .chart-legend .legend-title,
     :host ::ng-deep .chart-legend .legend-title-text,
     :host ::ng-deep .chart-legend .legend-label,
     :host ::ng-deep .chart-legend .legend-label-text {
       color: var(--mat-sys-on-surface, var(--sys-on-surface)) !important;
       fill: var(--mat-sys-on-surface, var(--sys-on-surface)) !important;
     }`,
    `:host ::ng-deep .chart-legend .legend-labels.horizontal-legend {
      row-gap: 0;
      column-gap: 12px;
    }`,
    `:host ::ng-deep .chart-legend .legend-label-color {
      width: 12px !important;
      height: 12px !important;
      min-width: 12px;
      min-height: 12px;
      border-radius: 50% !important;
    }`
  ]
})
export class NumberOfProjectsChartWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chartHost') private chartHostRef?: ElementRef<HTMLElement>;
  type: 'Number' | 'CAPEX' = 'Number';
  chartTitle = 'Number of Future and Active Projects';
  yAxisLabel = 'Number of Projects';
  chartView: [number, number] = [700, 400];
  chartData: NgxStackedDatum[] = [];
  loading = false;
  error = '';
  legendDomain: string[] = [];
  legendColors!: ColorHelper;
  private readonly chartHeight = 400;
  private readonly destroy$ = new Subject<void>();
  readonly yTickFormatting = (value: number) => this.formatYAxisTick(value);
  readonly colorScheme = {
    name: 'projectMapScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: [
      '#1F77B4',
      '#FF7F0E',
      '#2CA02C',
      '#D62728',
      '#9467BD',
      '#8C564B',
      '#E377C2',
      '#7F7F7F',
      '#BCBD22',
      '#17BECF',
      '#F4A261',
      '#264653',
      '#E9C46A',
      '#2A9D8F',
      '#E76F51',
      '#6A4C93',
      '#FFB703'
    ]
  };

  constructor(private readonly dataService: ProjectStatsDataService) { }

  ngOnInit(): void {
    this.updateLegend([]);
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateChartView());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateChartView();
  }

  setType(type: 'Number' | 'CAPEX'): void {
    this.type = type;
    this.chartTitle = type === 'CAPEX'
      ? 'CAPEX of Future and Active Projects'
      : 'Number of Future and Active Projects';
    this.yAxisLabel = type === 'CAPEX' ? 'CAPEX of Projects ($Million)' : 'Number of Projects';
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    const source$ = this.type === 'CAPEX'
      ? this.dataService.getCapex()
      : this.dataService.getCounts();

    source$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (payload: any) => {
          const raw = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
          this.chartData = this.mapToNgxStacked(raw);
          this.updateLegend(this.chartData);
          setTimeout(() => this.updateChartView());
          this.loading = false;
        },
        error: () => {
          this.error = 'Unable to load chart data';
          this.chartData = [];
          this.updateLegend([]);
          this.loading = false;
        },
        complete: () => this.loading = false
      });
  }

  private updateLegend(data: NgxStackedDatum[]): void {
    const firstDatum = data[0];
    this.legendDomain = Array.isArray(firstDatum?.series)
      ? firstDatum.series.map(item => item.name)
      : [];

    this.legendColors = new ColorHelper(
      this.colorScheme,
      ScaleType.Ordinal,
      this.legendDomain,
      this.colorScheme
    );
  }

  private mapToNgxStacked(raw: any[]): NgxStackedDatum[] {
    if (!Array.isArray(raw) || !raw.length) {
      return [];
    }

    if (raw[0]?.series && raw[0]?.name) {
      return raw as NgxStackedDatum[];
    }

    const legacySeries = raw as LegacySeries[];
    const seriesOrder = legacySeries
      .map(series => String(series?.key || 'Series'));
    const byRegion = new Map<string, { name: string; value: number }[]>();

    legacySeries.forEach(series => {
      const seriesName = series?.key || 'Series';
      (series?.values || []).forEach(point => {
        const region = String(point?.x || 'Unknown');
        if (!byRegion.has(region)) {
          byRegion.set(region, []);
        }

        byRegion.get(region)!.push({
          name: seriesName,
          value: this.normalizeNumber(point?.y)
        });
      });
    });

    return Array.from(byRegion.entries()).map(([name, series]) => {
      const valueBySeries = new Map(series.map(item => [item.name, item.value]));
      return {
        name,
        series: seriesOrder.map(seriesName => ({
          name: seriesName,
          value: valueBySeries.get(seriesName) ?? 0
        }))
      };
    });
  }

  private normalizeNumber(value: unknown): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  private formatYAxisTick(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    if (this.type === 'CAPEX') {
      return `$${Math.round(value).toLocaleString()}`;
    }

    return `${Math.round(value).toLocaleString()}`;
  }

  private updateChartView(): void {
    const host = this.chartHostRef?.nativeElement;
    if (!host) {
      return;
    }

    const width = Math.floor(host.getBoundingClientRect().width);
    if (width > 0) {
      this.chartView = [width, this.chartHeight];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
